import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  // Redirect if user is already logged in
  if (user) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Auth error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (role) => {
    setLoading(true);
    try {
      if (role === "admin") {
        await login("admin@royaldornoch.com", "dornoch2024");
      } else {
        const randomId = Math.random().toString(36).substring(7);
        await register(`staff_${randomId}@royaldornoch.com`, "staff123", "Team Member");
      }
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Demo login error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-split">
      {/* Left side - Branding */}
      <div className="login-image">
        <img
          src="https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1200"
          alt="Golf links"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 p-12">
          {/* Royal Dornoch Logo */}
          <img
            src="https://customer-assets.emergentagent.com/job_clubhouse-standards/artifacts/tsx8cmf7_CMYK_RD%20small.jpg"
            alt="Royal Dornoch Golf Club"
            className="w-40 h-40 object-contain mb-8 bg-white rounded-full p-2"
          />
          <h1 className="text-4xl text-white text-center mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
            Staff Training Portal
          </h1>
          <p className="text-white/80 text-center text-lg max-w-md">
            Delivering excellence through knowledge and service
          </p>
          <div className="mt-8 flex items-center gap-4">
            <div className="h-px w-12 bg-white/40"></div>
            <span className="text-white/60 text-sm tracking-widest uppercase">Est. 1616</span>
            <div className="h-px w-12 bg-white/40"></div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-8 lg:p-12 bg-[var(--rd-cream)]">
        <div className="w-full max-w-md animate-fade-in-up">
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_clubhouse-standards/artifacts/tsx8cmf7_CMYK_RD%20small.jpg"
              alt="Royal Dornoch Golf Club"
              className="w-24 h-24 object-contain mb-4"
            />
            <h1 className="text-xl text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
              Staff Training
            </h1>
          </div>

          {/* Desktop header */}
          <div className="hidden lg:block mb-10">
            <h2 className="text-2xl text-[var(--rd-green)] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
              Welcome
            </h2>
            <p className="text-[hsl(var(--muted-foreground))]">
              Sign in to access your training modules
            </p>
          </div>

          <Tabs 
            defaultValue="login" 
            className="w-full"
            onValueChange={(value) => setIsLogin(value === "login")}
          >
            <TabsList className="grid w-full grid-cols-2 mb-8 bg-white">
              <TabsTrigger 
                value="login" 
                data-testid="login-tab" 
                className="data-[state=active]:bg-[var(--rd-green)] data-[state=active]:text-white"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                data-testid="register-tab" 
                className="data-[state=active]:bg-[var(--rd-green)] data-[state=active]:text-white"
              >
                Register
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.name@royaldornoch.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                    className="h-11 bg-white border-[hsl(var(--border))] focus:border-[var(--rd-green)] focus:ring-[var(--rd-green)]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                      className="h-11 pr-12 bg-white border-[hsl(var(--border))]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-[var(--rd-green)] hover:bg-[var(--rd-green-dark)] text-white font-medium"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>

                <div className="text-center">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[var(--rd-green)] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </Link>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Smith"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="register-name-input"
                    className="h-11 bg-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reg-email" className="text-sm font-medium">Email Address</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    placeholder="your.name@royaldornoch.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="register-email-input"
                    className="h-11 bg-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reg-password" className="text-sm font-medium">Password</Label>
                  <div className="relative">
                    <Input
                      id="reg-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="register-password-input"
                      className="h-11 pr-12 bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-[var(--rd-green)] hover:bg-[var(--rd-green-dark)] text-white font-medium"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? "Creating account..." : "Create Account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
